import React, { useState, useEffect } from 'react';
import { ArrowRight, ChevronRight, CheckCircle2, AlertTriangle, XCircle, ShieldCheck, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const questions = [
  {
    id: 'q1',
    question: 'How do your site managers usually send updates and photos?',
    options: [
      { label: 'Dedicated site management system', value: 'system' },
      { label: 'Email', value: 'email' },
      { label: 'WhatsApp or group chats', value: 'whatsapp' },
      { label: 'Phone calls only', value: 'phone' },
      { label: 'Paper forms or printed sheets', value: 'paper' },
    ],
  },
  {
    id: 'q2',
    question: 'If you needed a document from 3 months ago, how long would it take your team to find it?',
    options: [
      { label: "Under a minute — it\'s all in one system", value: 'instant' },
      { label: '5–15 minutes searching folders', value: 'minutes' },
      { label: '30+ minutes across emails and drives', value: 'long' },
      { label: 'Hours — or it might be lost entirely', value: 'lost' },
    ],
  },
  {
    id: 'q3',
    question: 'How often do your team chase people for paperwork, photos or updates?',
    options: [
      { label: 'Rarely — everything comes in automatically', value: 'rarely' },
      { label: 'A few times a week', value: 'weekly' },
      { label: 'Daily', value: 'daily' },
      { label: "It\'s basically a full-time job", value: 'fulltime' },
    ],
  },
  {
    id: 'q4',
    question: 'If an audit happened tomorrow, how confident would you be in your records?',
    options: [
      { label: 'Very confident — everything is documented and ready', value: 'confident' },
      { label: 'Fairly confident but it would take some prep', value: 'mostly' },
      { label: 'Nervous — some things would be hard to find', value: 'nervous' },
      { label: 'Not confident at all', value: 'stressed' },
    ],
  },
  {
    id: 'q5',
    question: 'Can you see the live status of all your sites without calling anyone?',
    options: [
      { label: 'Yes — live dashboard shows everything', value: 'yes' },
      { label: 'Mostly, but some things need a call', value: 'mostly' },
      { label: 'No — I have to call or message to find out', value: 'no' },
      { label: "I rarely have a clear picture of all my sites", value: 'blind' },
    ],
  },
  {
    id: 'q6',
    question: 'How does your company track worker certifications and expiry dates?',
    options: [
      { label: 'Automated system with expiry alerts', value: 'automated' },
      { label: 'Spreadsheet we update manually', value: 'spreadsheet' },
      { label: 'Folder of scanned cards — checked occasionally', value: 'folder' },
      { label: "We don't track them systematically", value: 'none' },
    ],
  },
  {
    id: 'q7',
    question: 'How many active sites does your company run at once?',
    options: [
      { label: '1–10 sites', value: 'small' },
      { label: '11–30 sites', value: 'medium' },
      { label: '31–50 sites', value: 'large' },
      { label: '51–70 sites', value: 'xlarge' },
      { label: '71+ sites', value: 'enterprise' },
    ],
  },
  {
    id: 'q8',
    question: 'How many hours per week does your team spend chasing paperwork and updates?',
    options: [
      { label: 'Less than 1 hour', value: 'low' },
      { label: '1–3 hours', value: 'moderate' },
      { label: '4–8 hours', value: 'high' },
      { label: 'More than 8 hours', value: 'very_high' },
    ],
  },
];

const scoreWeights = {
  q1: { system: 0, email: 2, whatsapp: 3, phone: 3, paper: 4 },
  q2: { instant: 0, minutes: 2, long: 3, lost: 4 },
  q3: { rarely: 0, weekly: 2, daily: 3, fulltime: 4 },
  q4: { confident: 0, mostly: 2, nervous: 3, stressed: 4 },
  q5: { yes: 0, mostly: 2, no: 3, blind: 4 },
  q6: { automated: 0, spreadsheet: 2, folder: 3, none: 4 },
  q7: { small: 1, medium: 2, large: 3, xlarge: 4, enterprise: 4 },
  q8: { low: 0, moderate: 2, high: 3, very_high: 4 },
};

const isWellManaged = (answers) => {
  const goodAnswers = { q1: 'system', q2: 'instant', q3: 'rarely', q4: 'confident', q5: 'yes', q6: 'automated', q8: 'low' };
  let goodCount = 0;
  let totalChecked = 0;
  Object.entries(goodAnswers)?.forEach(([qId, goodVal]) => {
    if (answers?.[qId] !== undefined) {
      totalChecked++;
      if (answers?.[qId] === goodVal) goodCount++;
    }
  });
  return goodCount >= 5;
};

const getProblems = (answers) => {
  const problems = [];

  if (['whatsapp', 'email', 'phone', 'paper']?.includes(answers?.q1)) {
    const methodLabel = { whatsapp: 'WhatsApp', email: 'email', phone: 'phone calls', paper: 'paper forms' }?.[answers?.q1];
    problems?.push({
      id: 'communication', icon: XCircle, color: 'text-red-400',
      title: 'Lost information and no central record',
      detail: `Your team is using ${methodLabel} for site updates. Messages get buried, photos go missing and there's no searchable record of what was reported, when and by whom. In a dispute or audit, this creates serious gaps.`,
    });
  }

  if (['long', 'lost']?.includes(answers?.q2)) {
    problems?.push({
      id: 'documents', icon: AlertTriangle, color: 'text-amber-400',title: 'Document control and audit risk',
      detail: answers?.q2 === 'lost'? 'If documents could be lost entirely, your audit exposure is critical. Missing H&S docs, unsigned inductions and undocumented incidents are the most common compliance failures — and the hardest to defend without a central record.': 'Spending 30+ minutes searching for a document means your records are fragmented across drives, emails and folders. This creates real risk during audits and disputes when you need to produce evidence quickly.',
    });
  } else if (answers?.q2 === 'minutes') {
    problems?.push({
      id: 'documents', icon: AlertTriangle, color: 'text-amber-400',title: 'Document retrieval is slower than it should be',detail: 'Even 5–15 minutes searching for a document adds up across your team. A centralised document system means any record is found in seconds — not minutes.',
    });
  }

  if (['daily', 'fulltime']?.includes(answers?.q3)) {
    problems?.push({
      id: 'chasing', icon: XCircle, color: 'text-red-400',title: 'Time lost and admin costs',
      detail: answers?.q3 === 'fulltime'
        ? "If chasing paperwork feels like a full-time job, your team is spending significant time on admin that should be automated. At an average admin cost of £25–£35/hr, this likely costs your business thousands of pounds per month."
        : "Chasing paperwork and updates daily is a sign that your current process has no accountability built in. Every chase is time your team isn\'t spending on site — and a signal that information isn\'t flowing automatically.",
    });
  } else if (answers?.q3 === 'weekly') {
    problems?.push({
      id: 'chasing', icon: AlertTriangle, color: 'text-amber-400',title: 'Regular admin overhead from chasing',detail: 'Chasing a few times a week might feel manageable, but it adds up. Automated reminders and structured workflows eliminate most of this overhead entirely.',
    });
  }

  if (['nervous', 'stressed']?.includes(answers?.q4)) {
    problems?.push({
      id: 'audit', icon: XCircle, color: 'text-red-400',title: 'Compliance tracking and document expiry risk',
      detail: answers?.q4 === 'stressed'
        ? "Feeling unprepared for an audit is a serious risk signal. Without a complete, timestamped record of site activity, certifications, inductions and H&S documents — you're exposed. Audits can happen without notice and the burden of proof is on you.": "Needing prep time before an audit means your records aren't audit-ready by default. SiteProof keeps everything documented automatically — so you're always ready, not scrambling.",
    });
  }

  if (['no', 'blind']?.includes(answers?.q5)) {
    problems?.push({
      id: 'visibility', icon: AlertTriangle, color: 'text-amber-400',
      title: 'Lack of visibility across your sites',
      detail: answers?.q5 === 'blind' ? "Running sites without a clear picture of what's happening on each one is a significant operational risk. Issues escalate unnoticed, compliance gaps go undetected and you're always reacting rather than managing proactively.": "Having to call or message to find out what\'s happening on a site means you\'re not in control — you\'re dependent on people responding. A live dashboard gives you instant visibility without interrupting your team.",
    });
  }

  if (['spreadsheet', 'folder', 'none']?.includes(answers?.q6)) {
    problems?.push({
      id: 'certifications', icon: XCircle, color: 'text-red-400',title: 'Certification expiry risk and compliance tracking',
      detail: answers?.q6 === 'none'
        ? "Not tracking worker certifications systematically means expired cards could go unnoticed. Having an uncertified worker on site is a serious liability — and one of the most common compliance failures found during inspections."
        : answers?.q6 === 'folder'
        ? "Checking a folder of scanned cards occasionally means certifications can expire without anyone noticing. Automated expiry alerts ensure you're always ahead of renewals — not discovering problems during an inspection.": "Spreadsheets require manual updates and are easy to forget. When certifications expire without an automated alert, the risk falls entirely on you. One missed renewal can result in a prohibition notice.",
    });
  }

  if (['high', 'very_high']?.includes(answers?.q8)) {
    if (!problems?.find((p) => p?.id === 'chasing')) {
      problems?.push({
        id: 'admin_hours', icon: AlertTriangle, color: 'text-amber-400',
        title: 'Significant time lost to admin',
        detail: answers?.q8 === 'very_high' ? "More than 8 hours per week on chasing and paperwork is a major drain on your business. That's a full working day every week — time that could be spent managing sites, winning work or developing your team.": "4–8 hours per week on admin and chasing is above average. Structured workflows and automated reminders typically reduce this to under an hour for most teams.",
      });
    }
  }

  return problems;
};

// Single question slide component
const QuestionSlide = ({ question, questionIndex, totalQuestions, selectedValue, onSelect, visible, direction }) => {
  const [animClass, setAnimClass] = useState('');

  useEffect(() => {
    if (visible) {
      setAnimClass('question-enter-active');
    } else {
      setAnimClass('');
    }
  }, [visible]);

  if (!visible) return null;

  return (
    <div className={`question-slide ${animClass}`} style={{ animationDirection: direction === 'back' ? 'reverse' : 'normal' }}>
      {/* Question number + text */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs font-bold text-amber-400 uppercase tracking-widest">
            Question {questionIndex + 1} of {totalQuestions}
          </span>
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-white leading-snug">
          {question?.question}
        </h3>
      </div>

      {/* Options */}
      <div className="grid grid-cols-1 gap-3">
        {question?.options?.map((opt) => {
          const isSelected = selectedValue === opt?.value;
          return (
            <button
              key={opt?.value}
              onClick={() => onSelect(question?.id, opt?.value)}
              className={`group flex items-center justify-between w-full text-left px-5 py-4 rounded-xl border text-sm font-medium transition-all duration-200 ${
                isSelected
                  ? 'bg-amber-500/15 border-amber-500/60 text-amber-300 shadow-lg shadow-amber-500/10'
                  : 'bg-slate-800/60 border-slate-700/50 text-slate-300 hover:bg-slate-800 hover:border-slate-500 hover:text-white'
              }`}
            >
              <span>{opt?.label}</span>
              {isSelected ? (
                <CheckCircle2 className="w-5 h-5 text-amber-400 flex-shrink-0" />
              ) : (
                <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-slate-400 flex-shrink-0 transition-colors" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const SiteManagementHealthCheck = () => {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [currentQ, setCurrentQ] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [direction, setDirection] = useState('forward');
  const [visibleQ, setVisibleQ] = useState(0);

  const totalQuestions = questions?.length;
  // Progress based on current question index (not answered count) for smoother feel
  const progress = ((currentQ) / totalQuestions) * 100;
  const currentQuestion = questions?.[currentQ];
  const currentAnswer = answers?.[currentQuestion?.id];

  const handleSelect = (questionId, value) => {
    if (transitioning) return;
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);

    const nextIndex = currentQ + 1;
    if (nextIndex < totalQuestions) {
      setTransitioning(true);
      setDirection('forward');
      setTimeout(() => {
        setCurrentQ(nextIndex);
        setVisibleQ(nextIndex);
        setTransitioning(false);
      }, 320);
    }
    // If last question, just record the answer — submit button will appear
  };

  const handleBack = () => {
    if (currentQ === 0 || transitioning) return;
    setTransitioning(true);
    setDirection('back');
    setTimeout(() => {
      setCurrentQ(currentQ - 1);
      setVisibleQ(currentQ - 1);
      setTransitioning(false);
    }, 320);
  };

  const handleSubmit = () => {
    const answeredCount = Object.keys(answers)?.length;
    if (answeredCount < totalQuestions) return;
    setSubmitted(true);
  };

  const handleRetake = () => {
    setAnswers({});
    setSubmitted(false);
    setCurrentQ(0);
    setVisibleQ(0);
    setDirection('forward');
  };

  const totalScore = Object.entries(answers)?.reduce((sum, [qId, val]) => {
    return sum + (scoreWeights?.[qId]?.[val] ?? 0);
  }, 0);
  const maxScore = Object.values(scoreWeights)?.reduce((sum, opts) => sum + Math.max(...Object.values(opts)), 0);
  const scorePercent = Math.round((totalScore / maxScore) * 100);

  const problems = submitted ? getProblems(answers) : [];
  const wellManaged = submitted ? isWellManaged(answers) : false;

  const isLastQuestion = currentQ === totalQuestions - 1;
  const isLastAnswered = answers?.[questions?.[totalQuestions - 1]?.id] !== undefined;

  return (
    <section className="py-24 lg:py-32 bg-slate-950 border-t border-slate-800/60">
      <style>{`
        @keyframes slideInFromRight {
          from { opacity: 0; transform: translateX(40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideInFromLeft {
          from { opacity: 0; transform: translateX(-40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .question-slide {
          opacity: 0;
        }
        .question-enter-active {
          animation: slideInFromRight 0.32s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .question-enter-active[style*="reverse"] {
          animation: slideInFromLeft 0.32s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
      `}</style>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-1.5 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            <span className="text-xs font-semibold text-amber-400 uppercase tracking-widest">Free Assessment</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
            Site Management<br />
            <span className="text-amber-400">Health Check</span>
          </h2>
          <p className="text-slate-400 text-lg max-w-xl mx-auto leading-relaxed">
            Answer 8 quick questions to find out how much time and money your team may be losing to manual site management.
          </p>
        </div>

        {!submitted ? (
          <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl overflow-hidden">
            {/* Progress bar */}
            <div className="px-8 pt-7 pb-5 border-b border-slate-800/60">
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Progress</span>
                <span className="text-xs font-semibold text-slate-300">{currentQ + 1} of {totalQuestions}</span>
              </div>
              <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-400 rounded-full transition-all duration-500"
                  style={{ width: `${Math.max(progress, (1 / totalQuestions) * 100)}%` }}
                />
              </div>
            </div>

            {/* Single question area */}
            <div className="p-8 min-h-[340px] flex flex-col justify-center overflow-hidden">
              <QuestionSlide
                key={currentQ}
                question={currentQuestion}
                questionIndex={currentQ}
                totalQuestions={totalQuestions}
                selectedValue={currentAnswer}
                onSelect={handleSelect}
                visible={!transitioning}
                direction={direction}
              />
            </div>

            {/* Footer: back button + submit on last question */}
            <div className="px-8 pb-8 flex items-center gap-3">
              {currentQ > 0 && (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors px-4 py-3 rounded-xl border border-slate-700/50 hover:border-slate-600 bg-slate-800/40 hover:bg-slate-800"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
              )}

              {isLastQuestion && isLastAnswered && (
                <button
                  onClick={handleSubmit}
                  className="flex-1 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold py-3.5 px-6 rounded-xl text-base transition-all duration-200 shadow-xl shadow-amber-500/25"
                >
                  See My Results
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}

              {!isLastQuestion && currentAnswer && (
                <button
                  onClick={() => {
                    if (transitioning) return;
                    setTransitioning(true);
                    setDirection('forward');
                    setTimeout(() => {
                      setCurrentQ(currentQ + 1);
                      setVisibleQ(currentQ + 1);
                      setTransitioning(false);
                    }, 320);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-white font-semibold py-3.5 px-6 rounded-xl text-sm transition-all duration-200"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ) : wellManaged && problems?.length === 0 ? (
          /* Well managed — honest result */
          (<div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 mb-4">
                <ShieldCheck className="w-8 h-8 text-emerald-400" />
              </div>
              <p className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-1">Your Results</p>
              <h3 className="text-3xl font-bold text-white mb-3">You already have good systems in place</h3>
              <span className="inline-block text-sm font-bold px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                Well Managed
              </span>
            </div>
            <div className="bg-slate-900/60 rounded-xl p-5 mb-8 border border-slate-700/40">
              <p className="text-slate-300 text-sm leading-relaxed">
                Based on your answers, your team already has solid visibility, good document control and structured processes in place. Honestly, SiteProof may not be necessary for you right now — you're already ahead of most construction companies.
              </p>
            </div>
            <div className="bg-slate-900/40 rounded-xl p-4 border border-slate-700/30 mb-8">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-300">If you're growing your site count or want to consolidate everything into one platform, SiteProof can help you scale without adding admin overhead. But there's no pressure — you're in a good position.</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/company-registration')}
              className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold py-4 px-6 rounded-xl text-base transition-all duration-200 shadow-xl shadow-emerald-500/30 mb-3"
            >
              Explore SiteProof Anyway
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-xs text-slate-500 text-center">Card required. No charge during your 30-day trial. Cancel anytime.</p>
            <div className="mt-5 text-center">
              <button onClick={handleRetake} className="text-xs text-slate-500 hover:text-slate-300 transition-colors underline underline-offset-2">
                Retake assessment
              </button>
            </div>
          </div>)
        ) : (
          /* Problems identified */
          (<div className="rounded-2xl border border-slate-700/50 bg-slate-900/60 overflow-hidden">
            <div className={`p-8 border-b border-slate-800/60 ${problems?.length >= 4 ? 'bg-red-500/5' : problems?.length >= 2 ? 'bg-amber-500/5' : 'bg-slate-900/40'}`}>
              <div className="text-center">
                <p className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-2">
                  {problems?.length === 0 ? 'Assessment Complete' : problems?.length === 1 ? '1 Problem Identified' : `${problems?.length} Problems Identified`}
                </p>
                <h3 className="text-2xl font-bold text-white mb-4">
                  {problems?.length === 0
                    ? 'Your systems look fairly solid'
                    : problems?.length >= 4
                    ? 'Your team is losing significant time and carrying real compliance risk'
                    : 'Your team has some gaps worth addressing'}
                </h3>
                <div className="max-w-sm mx-auto">
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden mb-1.5">
                    <div
                      className={`h-full rounded-full transition-all duration-1000 ${scorePercent >= 60 ? 'bg-red-500' : scorePercent >= 35 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      style={{ width: `${scorePercent}%` }}
                    />
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-slate-600">Low risk</span>
                    <span className="text-xs text-slate-600">High risk</span>
                  </div>
                </div>
              </div>
            </div>
            {problems?.length > 0 && (
              <div className="p-8 space-y-4">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-5">Based on your answers, here's what we found:</p>
                {problems?.map((problem) => {
                  const ProblemIcon = problem?.icon;
                  return (
                    <div key={problem?.id} className="bg-slate-800/40 rounded-xl p-5 border border-slate-700/40">
                      <div className="flex items-start gap-3">
                        <ProblemIcon className={`w-5 h-5 ${problem?.color} flex-shrink-0 mt-0.5`} />
                        <div>
                          <p className="text-sm font-semibold text-white mb-1.5">{problem?.title}</p>
                          <p className="text-sm text-slate-400 leading-relaxed">{problem?.detail}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="px-8 pb-8">
              {problems?.length > 0 && (
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-5 mb-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-300 leading-relaxed">
                      SiteProof replaces WhatsApp, spreadsheets and paper with <span className="text-white font-semibold">one system</span> — giving you full visibility, automated certification alerts, a complete audit trail and less time spent chasing.
                    </p>
                  </div>
                </div>
              )}
              <button
                onClick={() => navigate('/company-registration')}
                className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold py-4 px-6 rounded-xl text-base transition-all duration-200 shadow-xl shadow-emerald-500/30 mb-3"
              >
                Start Your Free Trial
                <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-xs text-slate-500 text-center">Card required. No charge during your 30-day trial. Cancel anytime.</p>
              <div className="mt-5 text-center">
                <button onClick={handleRetake} className="text-xs text-slate-500 hover:text-slate-300 transition-colors underline underline-offset-2">
                  Retake assessment
                </button>
              </div>
            </div>
          </div>)
        )}
      </div>
    </section>
  );
};

export default SiteManagementHealthCheck;
